import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../demo-styles.css';

// ====== BASE API (ENV + fallback sur domaine courant) ======
const API_BASE = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/+$/, '');

// Ajoute "/api" si absent à la base REST
const apiRest = (path = '') => {
  const clean = String(path).replace(/^\/+/, '');
  return `${API_BASE}${API_BASE.endsWith('/api') ? '' : '/api'}/${clean}`;
};

// Convertit une URL http(s) → ws(s)
const httpToWs = (url) =>
  String(url || '')
    .replace(/^http:\/\//i, 'ws://')
    .replace(/^https:\/\//i, 'wss://');

// Construit l'URL WS absolue pour nos 2 relais
const wsUrlFor = (language) => {
  let suffix = null;
  if (language === 'amazigh') suffix = '/stt/ws/asr/amazigh';
  if (language === 'ma') suffix = '/swahili/ws/asr?lang=ary';
  if (!suffix) return null;
  const httpLike = `${API_BASE}${API_BASE.endsWith('/api') ? '' : '/api'}${suffix}`;
  return httpToWs(httpLike);
};

const SpeechToText = () => {
  const [searchParams] = useSearchParams();
  const language = searchParams.get('language') || 'tn';

  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Normal (record → POST) pour ma
  const [isNormalRecording, setIsNormalRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const normalChunksRef = useRef([]);

  // WebSocket / audio (uniquement pour amazigh)
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const microphoneRef = useRef(null);
  const audioBufferRef = useRef([]);
  const finalTranscriptsRef = useRef([]);
  const partialTranscriptRef = useRef('');

  // Web Speech API (pour toutes les langues sauf amazigh)
  const recognitionRef = useRef(null);

  const languageConfig = {
    tn: { title: 'Tunisian Arabic Speech-to-Text', description: '', lang: 'ar-TN' },
    ma: { title: 'Moroccan Arabic Speech-to-Text', description: '', lang: 'ar-MA' },
    eg: { title: 'Egyptian Arabic Speech-to-Text', description: '', lang: 'ar-EG' },
    amazigh: { title: 'Tamazight Speech-to-Text', description: '', lang: 'zgh' }
  };
  const currentConfig = languageConfig[language] || languageConfig.tn;

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => { cleanup(); };
    // eslint-disable-next-line
  }, [language]);

  const updateTranscript = () => {
    const allText = [
      ...finalTranscriptsRef.current,
      partialTranscriptRef.current
    ].filter(Boolean).join(' ');
    setTranscription(allText.trim());
  };

  const startRecordingWSGeneric = async (wsUrl) => {
    setTranscription('');
    setIsProcessing(true);
    audioBufferRef.current = [];
    finalTranscriptsRef.current = [];
    partialTranscriptRef.current = '';

    try {
      const ws = new window.WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              sampleRate: 16000,
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          });

          const audioContext = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 16000
          });
          audioContextRef.current = audioContext;

          const microphone = audioContext.createMediaStreamSource(stream);
          microphoneRef.current = microphone;

          const processor = audioContext.createScriptProcessor(4096, 1, 1);
          processorRef.current = processor;

          processor.onaudioprocess = (event) => {
            const inputBuffer = event.inputBuffer.getChannelData(0);
            const int16Array = new Int16Array(inputBuffer.length);
            for (let i = 0; i < inputBuffer.length; i++) {
              const sample = Math.max(-1, Math.min(1, inputBuffer[i]));
              int16Array[i] = sample * 32767;
            }
            audioBufferRef.current.push(...int16Array);

            if (audioBufferRef.current.length > 8000 && ws.readyState === WebSocket.OPEN) {
              const audioData = new Int16Array(audioBufferRef.current);
              ws.send(audioData.buffer);
              audioBufferRef.current = [];
            }
          };

          microphone.connect(processor);
          processor.connect(audioContext.destination);

          setIsRecording(true);
          setIsProcessing(false);
        } catch (error) {
          setTranscription('Error accessing microphone: ' + error.message);
          ws.close();
          setIsProcessing(false);
        }
      };

      ws.onmessage = (event) => {
        let data;
        try { data = JSON.parse(event.data); }
        catch (e) {
          partialTranscriptRef.current = String(event.data || '').trim();
          updateTranscript();
          return;
        }
        if (data.final) {
          if (data.text?.trim()) {
            finalTranscriptsRef.current.push(data.text.trim());
            partialTranscriptRef.current = '';
            updateTranscript();
          }
        } else if (data.text) {
          partialTranscriptRef.current = data.text.trim();
          updateTranscript();
        }
      };

      ws.onerror = (error) => {
        setTranscription('WebSocket error: ' + (error.message || 'Connection failed'));
        cleanup();
      };

      ws.onclose = () => { cleanup(); };

    } catch (err) {
      setIsRecording(false);
      setIsProcessing(false);
      setTranscription('Error: ' + (err.message || err));
    }
  };

  const stopRecordingWS = () => {
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      } else {
        cleanup();
      }
    } catch {
      cleanup();
    }
  };

  const cleanup = () => {
    if (processorRef.current) { try { processorRef.current.disconnect(); } catch {} processorRef.current = null; }
    if (microphoneRef.current) { try { microphoneRef.current.disconnect(); } catch {} microphoneRef.current = null; }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (wsRef.current) {
      try { if (wsRef.current.readyState === WebSocket.OPEN) wsRef.current.close(); } catch {}
      wsRef.current = null;
    }
    audioBufferRef.current = [];
    setIsRecording(false);
    setIsProcessing(false);
  };

  // --- START / STOP ---
  const startRecording = async () => {
    setTranscription('');

    // Amazigh et Moroccan Arabic utilisent WebSocket
    if (language === 'amazigh' || language === 'ma') {
      const wsUrl = wsUrlFor(language);
      if (!wsUrl) {
        setTranscription('No WS route for this language');
        return;
      }
      await startRecordingWSGeneric(wsUrl);
      return;
    }

    // Toutes les autres langues (tn, ma, eg) utilisent Web Speech API
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert('Browser SpeechRecognition not available. Please use a supported browser like Chrome.');
        return;
      }
      
      const rec = new SpeechRecognition();
      recognitionRef.current = rec;
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = currentConfig.lang;

      rec.onstart = () => {
        setIsRecording(true);
      };

      rec.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscription(finalTranscript + interimTranscript);
      };

      rec.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech' || event.error === 'audio-capture') {
          alert('No microphone detected or microphone permission denied. Please check your microphone settings.');
        }
        stopRecording();
      };

      rec.onend = () => {
        setIsRecording(false);
      };

      rec.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      alert('Error starting speech recognition: ' + error.message);
    }
  };

  const stopRecording = () => {
    // Amazigh et Moroccan Arabic utilisent WebSocket
    if (language === 'amazigh' || language === 'ma') {
      stopRecordingWS();
    } else {
      // Web Speech API pour les autres langues
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsRecording(false);
    }
  };

  // --- Normal recording (record → POST) pour ma ---
  const startNormalRecording = async () => {
    setTranscription('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      normalChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) normalChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(normalChunksRef.current, { type: 'audio/webm' });
        setIsProcessing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'recording.webm');
          const wsProto = window.location.protocol === 'https:' ? 'https:' : 'http:';
          const base = `${wsProto}//${window.location.host}`;
          const apiBase = (process.env.REACT_APP_API_URL || `${base}/api`).replace(/\/+$/, '');
          const url = `${apiBase}/swahili/asr?lang=ary`;
          const res = await fetch(url, { method: 'POST', body: formData });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          setTranscription(data?.text || data?.transcription || JSON.stringify(data));
        } catch (err) {
          setTranscription('Error: ' + err.message);
        } finally {
          setIsProcessing(false);
        }
      };
      mr.start();
      setIsNormalRecording(true);
    } catch (err) {
      setTranscription('Microphone error: ' + err.message);
    }
  };

  const stopNormalRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsNormalRecording(false);
  };

  // --- Upload fichier (HTTP POST) ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFile(file);
    setIsProcessing(true);
    setTranscription('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', language);

      const endpoint = apiRest('stt'); // => …/api/stt (sans double /api)

      const response = await fetch(endpoint, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setTranscription(data?.text || '');
    } catch (error) {
      setTranscription('Error: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearTranscription = () => {
    setTranscription('');
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isRtl = (text) => /[\u0600-\u06FF]/.test(text);

  return (
    <div className="demo-page-container">
      <div className="stt-grid">
        {/* Input Column */}
        <div className="stt-column">
          <h3 className="stt-column-title">Speech Input</h3>

          {/* Recording Section */}
          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ fontSize: '14px', color: '#555', marginBottom: '12px' }}>Record Audio</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>

              {/* Bouton LIVE (WS) — uniquement pour ma et amazigh */}
              {(language === 'ma' || language === 'amazigh') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing || isNormalRecording}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '10px 20px', backgroundColor: isRecording ? '#dc3545' : '#ED1C24',
                      color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600',
                      cursor: 'pointer', transition: 'all 0.2s', opacity: (isProcessing || isNormalRecording) ? 0.5 : 1
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: '20px' }}>
                      {isRecording ? 'stop' : 'graphic_eq'}
                    </span>
                    {isRecording ? 'Stop Live'
                      : language === 'amazigh' ? 'Live Tamazight'
                      : 'Live Moroccan Darija'}
                  </button>
                  {isRecording && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#dc3545', animation: 'pulse 1.5s infinite' }} />
                      <span style={{ color: '#666', fontSize: '13px' }}>Streaming...</span>
                    </div>
                  )}
                </div>
              )}

              {/* Bouton NORMAL (record → POST) — uniquement pour ma */}
              {language === 'ma' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button
                    onClick={isNormalRecording ? stopNormalRecording : startNormalRecording}
                    disabled={isProcessing || isRecording}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: isNormalRecording ? '#b45309' : '#92400e',
                      color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600',
                      cursor: 'pointer', transition: 'all 0.2s', opacity: (isProcessing || isRecording) ? 0.5 : 1
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: '20px' }}>
                      {isNormalRecording ? 'stop' : 'mic'}
                    </span>
                    {isNormalRecording ? 'Stop & Transcribe' : 'Start Moroccan Darija Recording'}
                  </button>
                  {isNormalRecording && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#b45309', animation: 'pulse 1.5s infinite' }} />
                      <span style={{ color: '#666', fontSize: '13px' }}>Recording...</span>
                    </div>
                  )}
                  {isProcessing && !isNormalRecording && (
                    <span style={{ fontSize: '13px', color: '#666' }}>Processing...</span>
                  )}
                </div>
              )}

              {/* Bouton unique pour tn et eg (Web Speech API) */}
              {language !== 'ma' && language !== 'amazigh' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '10px 20px', backgroundColor: isRecording ? '#dc3545' : '#ED1C24',
                      color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600',
                      cursor: 'pointer', transition: 'all 0.2s', opacity: isProcessing ? 0.7 : 1
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: '20px' }}>
                      {isRecording ? 'stop' : 'mic'}
                    </span>
                    {isRecording ? 'Stop Recording' : 'Start Recording'}
                  </button>
                  {isRecording && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#dc3545', animation: 'pulse 1.5s infinite' }} />
                      <span style={{ color: '#666' }}>Recording in progress...</span>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>

          {/* File Upload Section */}
          <div>
            <input
              type="file" accept="audio/*" id="audio-file"
              onChange={handleFileUpload} ref={fileInputRef}
              disabled={isProcessing} style={{ display: 'none' }}
            />
            <label
              htmlFor="audio-file"
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 16px', backgroundColor: '#f3f4f6',
                borderRadius: '8px', cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.7 : 1, fontSize: '14px'
              }}
            >
              <span className="material-icons" style={{ fontSize: '18px', color: '#666' }}>
                upload_file
              </span>
              Upload audio file (WAV, MP3, M4A)
            </label>

            {uploadedFile && (
              <div style={{
                marginTop: '12px', padding: '12px', backgroundColor: '#f9fafb',
                borderRadius: '8px', border: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', gap: '12px'
              }}>
                <span className="material-icons" style={{ color: '#555' }}>audiotrack</span>
                <div>
                  <div style={{ fontWeight: '500' }}>{uploadedFile.name}</div>
                  <div style={{ fontSize: '12px', color: '#777' }}>
                    {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                </div>
                {isProcessing && (
                  <div style={{
                    marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px',
                    fontSize: '14px'
                  }}>
                    <div style={{
                      width: '16px', height: '16px', border: '2px solid #e5e7eb',
                      borderTopColor: '#ED1C24', borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span>Processing...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Output Column */}
        <div className="stt-column">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 className="stt-column-title">Transcription</h3>
            {transcription && (
              <button
                onClick={clearTranscription}
                style={{
                  backgroundColor: 'transparent', color: '#6b7280', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px'
                }}
              >
                <span className="material-icons" style={{ fontSize: '16px' }}>delete</span>
                Clear
              </button>
            )}
          </div>

          <div style={{
            height: '300px', backgroundColor: transcription ? '#fff' : '#f9fafb',
            borderRadius: '8px', padding: '16px', border: transcription ? '1px solid #e5e7eb' : 'none',
            overflowY: 'auto', direction: isRtl(transcription) ? 'rtl' : 'ltr',
            textAlign: isRtl(transcription) ? 'right' : 'left'
          }}>
            {transcription ? (
              <p style={{ fontSize: '16px', lineHeight: 1.6, margin: 0 }}>{transcription}</p>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', height: '100%', color: '#9ca3af', textAlign: 'center'
              }}>
                <span className="material-icons" style={{ fontSize: '32px', marginBottom: '8px' }}>text_fields</span>
                <p style={{ margin: 0 }}>No transcription yet. Record or upload audio to see the result here.</p>
              </div>
            )}
          </div>

          {transcription && (
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { navigator.clipboard.writeText(transcription); alert('Transcription copied to clipboard!'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb', borderRadius: '6px',
                  fontSize: '14px', cursor: 'pointer'
                }}
              >
                <span className="material-icons" style={{ fontSize: '16px' }}>content_copy</span>
                Copy to clipboard
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Global Styles */}
      <style>{`
        @keyframes pulse { 0%{opacity:.5} 50%{opacity:1} 100%{opacity:.5} }
        @keyframes spin { to { transform: rotate(360deg) } }
      `}</style>
    </div>
  );
};

export default SpeechToText;
